
import {defineComponent} from "vue";
import {defineRule, Form} from "vee-validate";
import {useToast} from "vue-toastification";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import BaseOffcanvasButtonClose from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonClose.vue";
import InputText from "@/components/UI/InputText.vue";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import {
  CustomerProductCategoryRestDtoModel
} from "@/models/api/pharma-cpc-product-category-mgmt/CustomerProductCategoryRestDtoModel";
import {ProductCategoryRestService} from "@/services/rest/cpc-product-category-mgmt/ProductCategoryRestService";
import {
  CustomerProductCategoryOverviewRestDto,
  CustomerProductCategoryRestDto
} from "@/api/pharma-cpc-product-category-mgmt/models";
import {max} from "@vee-validate/rules";
import SelectCategories from "@/components/layouts/catalog/SelectCategories.vue";
import {PropType} from "vue/dist/vue";
import {AutoSaveFeedbackStatusEnum} from "@/models/enum/AutoSaveFeedbackStatusEnum";
import AutoSaveFeedback from "@/components/UI/AutoSaveFeedback.vue";
import I18n from "@/modules/i18n";

// Define custom VeeValidate rule for parent_code or dp_managed_parent_category_code
defineRule('parent_required', (value: string | null, {otherField}: { otherField: string | null }) => {
  if (!value && !otherField) {
    return I18n.global.t('parentRequired');
  }

  return true;
});

export default defineComponent({
  name: "CustomerCategory",
  emits: ['edit'],
  components: {
    Form,
    AutoSaveFeedback,
    SelectCategories,
    InputText,
    BaseOffcanvasButtonClose,
    AlertError2
  },
  props: {
    code: {
      type: String,
      required: false,
    },
    mode: {
      type: String,
      required: true,
    },
    allCustomerCategories: {
      type: Array as PropType<CustomerProductCategoryOverviewRestDto[]>,
      required: true,
    },
    allLanguages: {
      type: Array as PropType<string[]>,
      required: true
    },
  },
  data() {
    return {
      category: CustomerProductCategoryRestDtoModel.createWithDefaults() as CustomerProductCategoryRestDto,
      autoSaveFeedbackStatus: AutoSaveFeedbackStatusEnum.NONE,
      categoryCode: null as string | null,
      categoryMode: PageDetailModeEnum.NEW as PageDetailModeEnum,
      dpManagedParents: [] as string[],
      toaster: useToast(),
      pageUI: UIStateDto.createWithDefaults(),
    }
  },
  mounted() {
    this.reloadContent();
  },
  computed: {
    PageDetailModeEnum() {
      return PageDetailModeEnum;
    },
    AutoSaveFeedbackStatusEnum() {
      return AutoSaveFeedbackStatusEnum;
    },
    excludedCategoryCodes(): string[] {
      return this.allCustomerCategories
        .map(category => category.code);
    },
    customerCategories(): { code: string, description: string }[] {
      return this.allCustomerCategories
        .filter(category => category.code !== this.categoryCode)
        .map(category => {
          return {
            code: category.code,
            description: category.dp_path_by_name
          };
        });
    }
  },
  methods: {
    max,
    async submitData() {
      this.pageUI
        .clearError()
        .setNotReady();

      this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.BUSY;

      try {
        if (this.categoryMode === PageDetailModeEnum.NEW) {
          this.categoryCode = await ProductCategoryRestService.getInstance().addCustomerProductCategory(this.category);
          this.categoryMode = PageDetailModeEnum.EDIT;
        } else {
          await ProductCategoryRestService.getInstance().updateCustomerProductCategory(this.category, this.categoryCode);
        }
        this.category = await ProductCategoryRestService.getInstance().getCustomerProductCategory(this.categoryCode);
        this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.NONE;
        this.$emit('edit');
        await this.afterSave();
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },
    async reloadContent() {
      this.pageUI
        .clearError()
        .setNotReady();
      try {
        if (this.mode === PageDetailModeEnum.EDIT) {
          this.categoryMode = PageDetailModeEnum.EDIT;
          this.categoryCode = this.code!;
        }

        if (this.categoryMode === PageDetailModeEnum.EDIT) {
          this.category = await ProductCategoryRestService.getInstance().getCustomerProductCategory(this.categoryCode);
          this.dpManagedParents = this.category.dp_managed_parent_category_code ? [this.category.dp_managed_parent_category_code] : [];
        }
        this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.NONE;
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },
    async afterSave(): Promise<void> {
      this.toaster.success(this.$t('savedSuccessfully'));
    },
    setDpManagedCategory(category_code: string): void {
      this.dpManagedParents = [category_code];
      this.category.dp_managed_parent_category_code = category_code;
      this.category.parent_code = null;
      this.onCategoryChanged();
    },
    removeDpManagedCategory(): void {
      this.dpManagedParents = [];
      this.category.dp_managed_parent_category_code = null;
      this.onCategoryChanged();
    },
    setParentCustomerCategory(code: string): void {
      if (code) {
        this.dpManagedParents = [];
        this.category.dp_managed_parent_category_code = null;
      }
      this.onCategoryChanged();
    },
    onCategoryChanged(): void {
      this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.UNSAVED_CHANGES;
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
        .create()
        .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .catch(exceptions, true);
    },
  },
});
